// Emotion / expression layer for the avatar. An expression is a small set of
// offsets layered on top of the saved face config (smile / eye openness / brow
// lift) so the twin can look warm, thoughtful, concerned, etc. without changing
// the persisted configuration.

export type Expression = 'neutral' | 'warm' | 'joyful' | 'thinking' | 'concerned';

export interface ExpressionOffsets {
  smile: number;   // added to config.smile  (−1..1)
  eyeOpen: number; // added to config.eyeOpen (0..1, clamped)
  brow: number;    // brow lift, −1 (furrow) .. 1 (raise)
}

export const EXPRESSIONS: Record<Expression, ExpressionOffsets> = {
  neutral:   { smile: 0.0,  eyeOpen: 0.0,  brow: 0.0 },
  warm:      { smile: 0.32, eyeOpen: -0.05, brow: 0.08 },
  joyful:    { smile: 0.6,  eyeOpen: -0.12, brow: 0.18 },
  thinking:  { smile: -0.05, eyeOpen: -0.08, brow: -0.18 },
  concerned: { smile: -0.28, eyeOpen: 0.06,  brow: -0.3 },
};

// Lightweight, dependency-free sentiment → expression. Works across FR/EN/ES by
// scanning for cue words; falls back to neutral. Good enough to give the face a
// fitting cast based on what the twin just said.
const POS = /\b(merci|génial|super|content|heureux|bravo|parfait|excellent|formidable|j'?adore|cool|great|happy|glad|wonderful|awesome|love|perfect|thanks|amazing|gracias|feliz|genial|perfecto|encantado)\b/i;
const JOY = /(!{2,}|😂|😄|😁|🎉|❤️|🥳)/;
const NEG = /\b(désolé|malheureusement|problème|inquiet|difficile|triste|peur|erreur|hélas|sorry|unfortunately|problem|worried|difficult|sad|afraid|issue|concern|lo siento|problema|preocupa|triste|difícil)\b/i;
const THINK = /\b(peut-être|je pense|il me semble|hmm|complexe|dépend|considér|perhaps|i think|it seems|maybe|depends|complex|consider|not sure|quizás|creo que|depende)\b/i;

export function sentimentToExpression(text: string): Expression {
  const t = (text || '').slice(0, 600);
  if (!t.trim()) return 'neutral';
  if (NEG.test(t)) return 'concerned';
  if (JOY.test(t)) return 'joyful';
  if (POS.test(t)) return 'warm';
  if (THINK.test(t)) return 'thinking';
  return 'neutral';
}
